import React, {
    memo,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { FiltersProvider } from '@Sage/ssm-web5';
import { COMPANIES } from 'constants/Entities';
import { EntityFiltersActions, EntityListSelectActions, ServerListActions } from 'actions';
import MarketingStatusCell, { MARKETING_STATUS } from 'components/TableCells/MarketingStatusCell';
import { getLiteral } from 'utils/getLiteral';
import FiltersLayout from 'containers/components/layouts/FiltersLayout';
import EntityMenu from 'containers/components/EntityMenu';
import NewEntityList from 'containers/components/NewEntityList';
import SearchCompanies from 'containers/companies/components/SearchCompanies';
import CONFIG from './RecipientsAccountsTableConfig';
import {
    getMarketingStatusListFromState,
    getSubscribedMarketingStatusOption,
    hasSubscribedFilter,
    INITIAL_SUBSCRIBED_FILTER_VALUE,
    MARKETING_STATUS_LIST,
} from './marketingStatusFilterUtils';

const CACHE_ID = 'recipients_accounts_list';
const FILTER_KEY = 'recipientsAccounts';

function mapStateToProps(state) {
    const selection = state.entityListSelect[COMPANIES.entity];
    const selected = selection?.selected || {};
    const includedIds = Object.keys(selected).filter((id) => selected[id] === true);

    return {
        allSelected: selection?.allSelected,
        bulkSelectedEntities: selection?.total ?? 0,
        filters: state.entityFilters?.[FILTER_KEY]?.filters,
        marketingStatusList: getMarketingStatusListFromState(state),
        selectedIds: includedIds,
        shouldClearWeb5Checkboxes: selection?.shouldClearWeb5Checkboxes,
        spamchimp: state?.config?.flagsmith?.spamchimp,
        canEdit: !!state?.config?.permission?.crud_permission?.[COMPANIES.permission]?.update,
    };
}

const mapDispatchToProps = (dispatch) => ({
    onEntitySelect: bindActionCreators(EntityListSelectActions, dispatch).onEntitySelect,
    onEntitySelectAll: bindActionCreators(EntityListSelectActions, dispatch).onEntitySelectAll,
    changeFilter: bindActionCreators(EntityFiltersActions, dispatch).changeFilter,
    getMarketingStatusList: bindActionCreators(ServerListActions, dispatch).getList,
    setShouldClearWeb5Checkboxes: bindActionCreators(EntityListSelectActions, dispatch)
        .setShouldClearWeb5Checkboxes,
});

const RecipientsAccountsTab = memo(
    ({
        allSelected,
        changeFilter,
        filters,
        getMarketingStatusList,
        marketingStatusList,
        selectionToken,
        onSelectionChange,
        shouldApplyInitialMarketingStatus,
        onInitialMarketingStatusApplied,
        onNonSubscribedSelectionChange,
        shouldClearWeb5Checkboxes,
        spamchimp,
        canEdit,
        onEntitySelect,
        onEntitySelectAll,
        setShouldClearWeb5Checkboxes,
    }) => {
        const tableRef = useRef(null);
        const areAllChecked = useRef(false);
        const [nonSubscribedCount, setNonSubscribedCount] = useState(0);

        const subscribedMarketingStatusOption = useMemo(
            () => getSubscribedMarketingStatusOption(marketingStatusList),
            [marketingStatusList],
        );

        useEffect(() => {
            if (!shouldApplyInitialMarketingStatus || subscribedMarketingStatusOption) {
                return;
            }

            getMarketingStatusList(MARKETING_STATUS_LIST).catch(() => null);
        }, [
            getMarketingStatusList,
            shouldApplyInitialMarketingStatus,
            subscribedMarketingStatusOption,
        ]);

        useLayoutEffect(() => {
            const updates = [];
            let didApplyInitialMarketingStatus = false;

            if (filters?.entityShowSelected?.value !== 'marketingCampaigns') {
                updates.push({
                    filter: {
                        id: 'entityShowSelected',
                        hideForCount: true,
                        invisible: true,
                        noCacheCheck: true,
                    },
                    value: 'marketingCampaigns',
                });
            }

            if (
                shouldApplyInitialMarketingStatus &&
                subscribedMarketingStatusOption &&
                !hasSubscribedFilter(filters?.marketingStatus?.value)
            ) {
                updates.push({
                    filter: {
                        id: 'marketingStatus',
                        dataType: 'singleValueList',
                        description: getLiteral('label_marketing_status'),
                        hideForCount: true,
                        inputAttrs: {
                            list: 'tblMarketingStatus',
                            isMulti: true,
                        },
                    },
                    value: INITIAL_SUBSCRIBED_FILTER_VALUE,
                    completeValues: [subscribedMarketingStatusOption],
                });
                didApplyInitialMarketingStatus = true;
            }

            if (!updates.length) {
                return;
            }

            updates.forEach((update, index) => {
                const isLast = index === updates.length - 1;
                changeFilter({
                    entity: COMPANIES,
                    filter: update.filter,
                    value: update.value,
                    completeValues: update.completeValues,
                    filterKey: FILTER_KEY,
                    refresh: isLast,
                });
            });

            if (didApplyInitialMarketingStatus) {
                onInitialMarketingStatusApplied?.('accounts');
            }
        }, [
            changeFilter,
            filters?.entityShowSelected?.value,
            filters?.marketingStatus?.value,
            subscribedMarketingStatusOption,
            onInitialMarketingStatusApplied,
            shouldApplyInitialMarketingStatus,
        ]);

        useEffect(() => {
            onNonSubscribedSelectionChange?.(nonSubscribedCount);
        }, [nonSubscribedCount, onNonSubscribedSelectionChange]);

        useEffect(() => {
            if (shouldClearWeb5Checkboxes) {
                if (tableRef?.current?.clearCheckboxes) tableRef.current.clearCheckboxes();
                setShouldClearWeb5Checkboxes(COMPANIES, false);
                setNonSubscribedCount(0);
            }
        }, [setShouldClearWeb5Checkboxes, shouldClearWeb5Checkboxes]);

        useEffect(() => {
            areAllChecked.current = allSelected;
        }, [allSelected]);

        const handleOnRef = useCallback((ref) => {
            tableRef.current = ref;
        }, []);

        const onClickCheckbox = useCallback(
            ({ id, isColumn, value, checked, data, rowIndex, total }) => {
                onSelectionChange?.('accounts');

                if (isColumn) {
                    onEntitySelectAll(COMPANIES, value, checked, total, FILTER_KEY);
                    // On select-all, reset non-subscribed counter
                    setNonSubscribedCount(0);
                } else {
                    onEntitySelect(COMPANIES, id, value, data, rowIndex, checked);
                    const statusId = Number(
                        data?.marketingStatusId ?? data?.MarketingStatusId ?? 0,
                    );
                    if (statusId !== MARKETING_STATUS.SUBSCRIBED) {
                        setNonSubscribedCount((prev) =>
                            checked ? prev + 1 : Math.max(0, prev - 1),
                        );
                    }
                }
            },
            [onEntitySelect, onEntitySelectAll, onSelectionChange],
        );

        const config = useMemo(
            () => CONFIG({ onClickCheckbox, selectionToken, spamchimp, canEdit }),
            [onClickCheckbox, selectionToken, spamchimp, canEdit],
        );

        const getCheckedRows = useCallback(
            (data) => {
                const columnName = config?.columnDefs?.[0]?.colId;
                if (!columnName) {
                    return {};
                }

                const checkedRows = data.reduce((acc, current) => {
                    if (!current) {
                        return acc;
                    }

                    if (!acc[columnName]) {
                        acc[columnName] = {
                            rows: {},
                            areAllChecked: areAllChecked.current,
                            lazyAreAllChecked: areAllChecked.current,
                        };
                    }

                    acc[columnName].rows[current.Id] = current.selected === '1';
                    return acc;
                }, {});

                if (checkedRows[columnName]) {
                    checkedRows[columnName].areAllChecked = areAllChecked.current;
                    checkedRows[columnName].lazyAreAllChecked = areAllChecked.current;
                }

                return checkedRows;
            },
            [config],
        );

        if (!config) return null;

        return (
            <FiltersProvider>
                <FiltersLayout config={config}>
                    <EntityMenu
                        entity={COMPANIES}
                        filterKey={config.filterKey}
                        withFilters={true}
                        quickFilters={config.quickFilters}
                        readOnlyQuickFilters={config.readOnlyQuickFilters}
                        useQuickView={config.useQuickView}
                        searchComponent={<SearchCompanies filterKey={config.filterKey} />}
                        withBulkOperations={false}
                        hideDefaultActions={true}
                        canMassiveUpdate={false}
                        canMassiveDelete={false}
                        canMerge={false}
                    />
                    <NewEntityList
                        id="fm-grid-recipients-accounts"
                        entity={COMPANIES}
                        config={config}
                        cacheId={CACHE_ID}
                        filterKey={config.filterKey}
                        initOnReady={true}
                        useLazyLoad={true}
                        useCache={true}
                        useSort={true}
                        useSelectColumns={true}
                        useFieldSelector={false}
                        discardCount={true}
                        domLayout="normal"
                        components={{ MarketingStatusCell }}
                        handleOnRef={handleOnRef}
                        getCheckedRows={getCheckedRows}
                        preventInitEntitySelect={!!selectionToken}
                    />
                </FiltersLayout>
            </FiltersProvider>
        );
    },
);

export default connect(mapStateToProps, mapDispatchToProps)(RecipientsAccountsTab);
